export const AUTH_CALLBACK_ERROR_QUERY_PARAM = 'authError';

const POST_LOGIN_REDIRECT_STORAGE_KEY = 'pesto:post-login-redirect';

export type AuthCallbackErrorCode = 'missing_hash' | 'parse_failed' | 'missing_token' | 'callback_failed';

const DEFAULT_ERROR_MESSAGES: Record<AuthCallbackErrorCode, string> = {
  missing_hash: 'Missing auth callback hash.',
  parse_failed: 'Failed to parse auth callback hash.',
  missing_token: 'Auth callback did not include an access token.',
  callback_failed: 'Auth callback failed.',
};

export class AuthCallbackError extends Error {
  readonly code: AuthCallbackErrorCode;

  constructor(code: AuthCallbackErrorCode, cause?: unknown) {
    super(DEFAULT_ERROR_MESSAGES[code], cause === undefined ? undefined : { cause });
    this.code = code;
  }
}

export const createAuthCallbackError = (code: AuthCallbackErrorCode, cause?: unknown) =>
  new AuthCallbackError(code, cause);

export const isAuthCallbackError = (value: unknown): value is AuthCallbackError => value instanceof AuthCallbackError;

export const clearPostLoginRedirect = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  } catch {}
};

export const rememberPostLoginRedirect = (path: string) => {
  if (typeof window === 'undefined' || !path) {
    return;
  }

  try {
    window.sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, path);
  } catch {}
};

export const consumePostLoginRedirect = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);

    return value;
  } catch {
    return null;
  }
};

type SafeRedirectOptions = {
  loginPath?: string;
  loginCallbackPath?: string;
};

const toSameOriginUrl = (path: string): URL | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!path.startsWith('/') || path.startsWith('//')) {
    return null;
  }

  try {
    const url = new URL(path, window.location.origin);

    if (url.origin !== window.location.origin) {
      return null;
    }

    return url;
  } catch {
    return null;
  }
};

const normalizePathname = (path?: string): string | null => {
  if (!path) {
    return null;
  }

  return toSameOriginUrl(path)?.pathname ?? null;
};

export const sanitizePostLoginRedirect = (candidate: string | null | undefined, options: SafeRedirectOptions = {}) => {
  if (!candidate) {
    return null;
  }

  const value = candidate.trim();

  if (!value) {
    return null;
  }

  const url = toSameOriginUrl(value);

  if (!url) {
    return null;
  }

  const loginPathname = normalizePathname(options.loginPath);
  const loginCallbackPathname = normalizePathname(options.loginCallbackPath);

  if (loginPathname && url.pathname === loginPathname) {
    return null;
  }

  if (loginCallbackPathname && url.pathname === loginCallbackPathname) {
    return null;
  }

  return `${url.pathname}${url.search}${url.hash}`;
};

export const getAuthCallbackLoginErrorPath = ({
  loginPath,
  errorQueryParam = AUTH_CALLBACK_ERROR_QUERY_PARAM,
  errorCode = 'callback_failed',
}: {
  loginPath: string;
  errorQueryParam?: string;
  errorCode?: AuthCallbackErrorCode;
}) => {
  const fallbackLoginPath = loginPath || '/';

  if (typeof window === 'undefined') {
    const separator = fallbackLoginPath.includes('?') ? '&' : '?';

    return `${fallbackLoginPath}${separator}${errorQueryParam}=${errorCode}`;
  }

  const url = new URL(fallbackLoginPath, window.location.origin);
  url.searchParams.set(errorQueryParam, errorCode);

  return `${url.pathname}${url.search}${url.hash}`;
};
